import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Trash2 } from "lucide-react";
import { CustomerPicker } from "@/features/customers/components/CustomerPicker";
import { usePaymentTerms, useVatRates } from "@/shared/hooks/useMetaData";
import type { CreateSalesInvoiceRequest } from "../services/invoicingApi";

interface InvoiceItemDraft {
  id: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  discountAmount: string;
  variety: string;
  chargeType: string;
}

interface CreateSalesInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting?: boolean;
  onSubmit: (payload: CreateSalesInvoiceRequest) => void;
}

const createEmptyItem = (): InvoiceItemDraft => ({
  id: String(Date.now() + Math.random()),
  description: "",
  quantity: "1",
  unit: "1",
  unitPrice: "0",
  discountAmount: "0",
  variety: "",
  chargeType: "5",
});

export const CreateSalesInvoiceDialog = ({
  open,
  onOpenChange,
  isSubmitting = false,
  onSubmit,
}: CreateSalesInvoiceDialogProps) => {
  const paymentTermsQuery = usePaymentTerms();
  const vatRatesQuery = useVatRates();
  const defaultVatRate =
    vatRatesQuery.data?.find((rate) => rate.name.toLowerCase().includes("standard"))?.rate ??
    vatRatesQuery.data?.[0]?.rate ??
    0;

  const [customerId, setCustomerId] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [taxRate, setTaxRate] = useState("0");
  const [discountAmount, setDiscountAmount] = useState("0");
  const [terms, setTerms] = useState("Net30");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<InvoiceItemDraft[]>([createEmptyItem()]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const resetId = window.setTimeout(() => {
      const today = new Date().toISOString().slice(0, 10);
      setCustomerId("");
      setInvoiceDate(today);
      setDueDate(today);
      setTaxRate(String(defaultVatRate));
      setDiscountAmount("0");
      setTerms("Net30");
      setNotes("");
      setItems([createEmptyItem()]);
    }, 0);

    return () => window.clearTimeout(resetId);
  }, [defaultVatRate, open]);

  const addItem = () => {
    setItems((current) => [...current, createEmptyItem()]);
  };

  const removeItem = (itemId: string) => {
    setItems((current) => {
      if (current.length === 1) {
        return current;
      }
      return current.filter((item) => item.id !== itemId);
    });
  };

  const updateItem = (itemId: string, field: keyof InvoiceItemDraft, value: string) => {
    setItems((current) =>
      current.map((item) => (item.id === itemId ? { ...item, [field]: value } : item))
    );
  };

  const estimatedTotal = useMemo(() => {
    const itemsTotal = items.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      const unitPrice = Number(item.unitPrice) || 0;
      const lineDiscount = Number(item.discountAmount) || 0;
      return sum + quantity * unitPrice - lineDiscount;
    }, 0);

    const headerDiscount = Number(discountAmount) || 0;
    const tax = (itemsTotal - headerDiscount) * ((Number(taxRate) || 0) / 100);
    return Math.max(itemsTotal - headerDiscount + tax, 0);
  }, [items, discountAmount, taxRate]);

  const handleSubmit = () => {
    if (!customerId || !invoiceDate || !dueDate || !items.length) {
      return;
    }

    const normalizedItems = items
      .filter((item) => item.description.trim())
      .map((item) => ({
        description: item.description.trim(),
        quantity: Number(item.quantity) || 0,
        unit: Number(item.unit) || 1,
        unitPrice: Number(item.unitPrice) || 0,
        discountAmount: Number(item.discountAmount) || 0,
        variety: item.variety.trim() || undefined,
        chargeType: Number(item.chargeType),
      }));

    if (!normalizedItems.length) {
      return;
    }

    onSubmit({
      customerId,
      invoiceDate: new Date(invoiceDate).toISOString(),
      dueDate: new Date(dueDate).toISOString(),
      taxRate: Number(taxRate) || 0,
      discountAmount: Number(discountAmount) || 0,
      terms: terms || undefined,
      notes: notes.trim() || undefined,
      items: normalizedItems,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Create Sales Invoice</DialogTitle>
          <DialogDescription>Create a standalone AR invoice not tied to an order.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Customer</Label>
              <CustomerPicker value={customerId} onChange={setCustomerId} />
            </div>

            <div className="space-y-2">
              <Label>Invoice Date</Label>
              <DatePicker value={invoiceDate} onChange={setInvoiceDate} />
            </div>

            <div className="space-y-2">
              <Label>Due Date</Label>
              <DatePicker value={dueDate} onChange={setDueDate} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>VAT Rate (%)</Label>
              {vatRatesQuery.isLoading ? (
                <Input value="Loading VAT rates..." disabled />
              ) : (
                <Select value={taxRate} onValueChange={setTaxRate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select VAT rate" />
                  </SelectTrigger>
                  <SelectContent>
                    {vatRatesQuery.data?.map((rate) => (
                      <SelectItem key={`${rate.name}-${rate.rate}`} value={String(rate.rate)}>
                        {`${rate.name} (${rate.rate}%)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label>Discount Amount</Label>
              <Input type="number" min={0} step="0.01" value={discountAmount} onChange={(event) => setDiscountAmount(event.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>Payment Terms</Label>
              <Select value={terms} onValueChange={setTerms}>
                <SelectTrigger>
                  <SelectValue placeholder="Select terms" />
                </SelectTrigger>
                <SelectContent>
                  {paymentTermsQuery.data?.map((term) => (
                    <SelectItem key={term.value} value={term.value}>
                      {term.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Invoice Items</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                Add Item
              </Button>
            </div>

            <div className="space-y-3">
              {items.map((item, index) => (
                <div key={item.id} className="grid gap-2 rounded-md border p-3 md:grid-cols-12">
                  <Input
                    className="md:col-span-3"
                    placeholder={`Item ${index + 1} description`}
                    value={item.description}
                    onChange={(event) => updateItem(item.id, "description", event.target.value)}
                  />
                  <Input
                    className="md:col-span-1"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(event) => updateItem(item.id, "quantity", event.target.value)}
                  />
                  <Input
                    className="md:col-span-1"
                    type="number"
                    min={1}
                    placeholder="Unit"
                    value={item.unit}
                    onChange={(event) => updateItem(item.id, "unit", event.target.value)}
                  />
                  <Input
                    className="md:col-span-2"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Unit price"
                    value={item.unitPrice}
                    onChange={(event) => updateItem(item.id, "unitPrice", event.target.value)}
                  />
                  <Input
                    className="md:col-span-1"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Discount"
                    value={item.discountAmount}
                    onChange={(event) => updateItem(item.id, "discountAmount", event.target.value)}
                  />
                  <Select
                    value={item.chargeType}
                    onValueChange={(value) => updateItem(item.id, "chargeType", value)}
                  >
                    <SelectTrigger className="md:col-span-2">
                      <SelectValue placeholder="Revenue type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Product sale</SelectItem>
                      <SelectItem value="2">Propagation service</SelectItem>
                      <SelectItem value="3">Delivery service</SelectItem>
                      <SelectItem value="5">Other revenue</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="md:col-span-2 flex gap-2">
                    <Input
                      placeholder="Variety"
                      value={item.variety}
                      onChange={(event) => updateItem(item.id, "variety", event.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(item.id)}
                      disabled={items.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Optional notes" />
          </div>

          <div className="rounded-md border bg-muted/20 p-3 text-sm">
            Estimated total: {new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES" }).format(estimatedTotal)}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !customerId || !invoiceDate || !dueDate}>
            {isSubmitting ? "Creating..." : "Create Invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
